// ============================================
// FILE 12: /frontend/src/components/listings/PriceAlertButton.jsx
// PURPOSE: Price drop alert button
// ============================================

import { useState, useEffect } from 'react';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { priceAlertsAPI } from '../../api/priceAlerts.api';

export default function PriceAlertButton({ listingId, size = 'default', className = '' }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [hasAlert, setHasAlert] = useState(false);
  const [alertId, setAlertId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (user && listingId) {
      checkAlertStatus();
    } else {
      setChecking(false);
    }
  }, [user, listingId]);

  const checkAlertStatus = async () => {
    try {
      const response = await priceAlertsAPI.getUserPriceAlerts(user.id);
      const alert = response.alerts?.find((a) => a.listingId === listingId && !a.triggered);
      if (alert) {
        setHasAlert(true);
        setAlertId(alert.id);
      }
    } catch (error) {
      console.error('Failed to check alert status:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleToggleAlert = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      toast({
        variant: 'destructive',
        title: 'Login Required',
        description: 'Please login to set price alerts.',
      });
      return;
    }

    setLoading(true);

    try {
      if (hasAlert && alertId) {
        // Remove alert
        await priceAlertsAPI.deletePriceAlert(alertId);
        setHasAlert(false);
        setAlertId(null);
        toast({
          title: 'Alert Removed',
          description: 'You will no longer receive price drop notifications.',
        });
      } else {
        // Create alert
        const response = await priceAlertsAPI.createPriceAlert(listingId);
        setHasAlert(true);
        setAlertId(response.alert.id);
        toast({
          title: 'Alert Set',
          description: "We'll notify you when the price drops by 5% or more.",
        });
      }
    } catch (error) {
      console.error('Failed to toggle alert:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.message || 'Failed to manage price alert.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <Button variant="outline" size={size} disabled className={className}>
        <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
      </Button>
    );
  }

  return (
    <Button
      variant={hasAlert ? 'default' : 'outline'}
      size={size}
      onClick={handleToggleAlert}
      disabled={loading}
      className={className}
    >
      {loading ? (
        <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
      ) : hasAlert ? (
        <>
          <BellOff className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
          <span className="text-xs sm:text-sm">Alert On</span>
        </>
      ) : (
        <>
          <Bell className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
          <span className="text-xs sm:text-sm">Price Alert</span>
        </>
      )}
    </Button>
  );
}